import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api } from '@/utils/api';
import Layout from '@/components/Layout';
import EditarAlumnoDialog from '@/components/EditarAlumnoDialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  ClipboardList,
  Activity,
  Loader2,
  ChevronRight,
  FileDown,
  Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { getScenarioLabel, getActivityLabel } from '@/utils/labels';
import {
  ComparativaEntrevistas,
  AdherenciaRetest,
  PruebasFuncionalesDetalle,
  formatFecha,
  num,
} from '@/components/SeguimientoAlumno';
import {
  TITULO, ROTULO, BTN_VERDE, BTN_CLARO, colorEscenario, NOMBRE_PRUEBA,
} from '@/lib/estiloApp';

// Ficha del alumno (panel unificado, 01/11/2026): el lugar de trabajo de cada
// persona. Arriba quién es y qué escenario tiene, con las acciones a mano;
// abajo, pestañas para no mezclar todo en una sola columna larga.

const PESTANAS = [
  { key: 'resumen', label: 'Resumen' },
  { key: 'entrevistas', label: 'Entrevistas' },
  { key: 'pruebas', label: 'Pruebas' },
  { key: 'informes', label: 'Informes' },
];

function edadDesde(fechaNacimiento) {
  if (!fechaNacimiento) return null;
  const nac = new Date(`${String(fechaNacimiento).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(nac.getTime())) return null;
  const hoy = new Date();
  let edad = hoy.getFullYear() - nac.getFullYear();
  const antes = hoy.getMonth() < nac.getMonth()
    || (hoy.getMonth() === nac.getMonth() && hoy.getDate() < nac.getDate());
  if (antes) edad -= 1;
  return edad >= 0 && edad <= 120 ? edad : null;
}

/** Valor corto del último resultado de cada prueba, para el Resumen. */
function ultimasPruebas(pruebas) {
  const salida = [];
  const ultimaDe = (clave) => pruebas.find((m) => m.resultados?.[clave]);
  const c6m = ultimaDe('c6m');
  if (c6m) {
    const r = c6m.resultados.c6m;
    salida.push({
      clave: 'c6m', nombre: 'Caminata de 6 minutos', fecha: c6m.created_at,
      valor: r.distancia_m != null ? `${r.distancia_m} m` : '—',
      detalle: r.porcentaje_teorico != null ? `${r.porcentaje_teorico} % del teórico` : null,
      aviso: (r.avisos || []).length > 0 || r.bajo_lin === true,
    });
  }
  const ast = ultimaDe('astrand');
  if (ast) {
    const r = ast.resultados.astrand;
    salida.push({
      clave: 'astrand', nombre: 'Escalón de Åstrand', fecha: ast.created_at,
      valor: r.vo2max_ml_kg_min != null ? String(r.vo2max_ml_kg_min) : '—',
      detalle: r.vo2max_ml_kg_min != null ? 'ml/kg/min estimado' : 'No calculable',
      aviso: !!r.betabloqueante,
    });
  }
  const sp = ultimaDe('sentarse_pararse');
  if (sp) {
    const r = sp.resultados.sentarse_pararse;
    salida.push({
      clave: 'sp', nombre: 'Sentarse-pararse 30 s', fecha: sp.created_at,
      valor: r.repeticiones != null ? String(r.repeticiones) : '—', detalle: 'repeticiones', aviso: false,
    });
  }
  const hg = ultimaDe('handgrip');
  if (hg) {
    const r = hg.resultados.handgrip;
    salida.push({
      clave: 'hg', nombre: 'Dinamometría de mano', fecha: hg.created_at,
      valor: r.mejor_kg != null ? `${r.mejor_kg} kg` : '—', detalle: 'mejor mano',
      aviso: r.por_debajo_de_corte === true,
    });
  }
  return salida;
}

/** Lo que corresponde hacer con esta persona, en lenguaje de sugerencia. */
function queTocaAhora(alumno, entrevistas, pruebas) {
  const items = [];
  if (entrevistas.length === 0) {
    items.push('Todavía no tiene entrevista: conviene arrancar por ahí.');
    return items;
  }
  if (alumno.apto_pendiente) {
    items.push(`Falta el apto médico que sugirió la entrevista del ${formatFecha(entrevistas[0].created_at)}. Cuando lo traiga, cargalo en esa entrevista.`);
  }
  if (alumno.estado_retest === 'vencido') {
    const d = Math.abs(alumno.dias_para_retest);
    items.push(`El retest venció hace ${d} día${d === 1 ? '' : 's'}.`);
  } else if (alumno.estado_retest === 'proximo') {
    items.push(alumno.dias_para_retest === 0
      ? 'El retest es hoy.'
      : `El retest es en ${alumno.dias_para_retest} día${alumno.dias_para_retest === 1 ? '' : 's'} (${formatFecha(alumno.proximo_retest)}).`);
  }
  if (pruebas.length === 0) {
    items.push('Todavía no tiene pruebas funcionales: sirven para saber desde dónde parte.');
  } else if (alumno.estado_retest === 'vencido' || alumno.estado_retest === 'proximo') {
    items.push(`Las últimas pruebas funcionales son del ${formatFecha(pruebas[0].created_at)}: conviene repetirlas con el retest.`);
  }
  return items;
}

/** Caja blanca con título en Anton. */
function Caja({ titulo, accion, children, className = '' }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 ${className}`}>
      {(titulo || accion) && (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          {titulo && <h3 className={`${TITULO} text-[19px] text-slate-900`}>{titulo}</h3>}
          {accion}
        </div>
      )}
      {children}
    </div>
  );
}

export default function AlumnoFichaPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [pruebasData, setPruebasData] = useState(null);
  const [errorPruebas, setErrorPruebas] = useState(null);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [descargando, setDescargando] = useState(false);
  const [editando, setEditando] = useState(false);

  const pestana = PESTANAS.some((p) => p.key === searchParams.get('tab'))
    ? searchParams.get('tab') : 'resumen';
  const irA = (key) => setSearchParams(key === 'resumen' ? {} : { tab: key }, { replace: true });

  // Informe de seguimiento: evolución de entrevistas y pruebas funcionales.
  const descargarSeguimiento = async () => {
    if (descargando) return;
    setDescargando(true);
    try {
      const { blob, filename } = await api.getInformeSeguimiento(id);
      api.downloadBlob(blob, filename);
    } catch (e) {
      toast.error(e?.message || 'No se pudo generar el informe de seguimiento');
    } finally {
      setDescargando(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.getAlumnoHistorial(id)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (!cancelled) toast.error(e?.message || 'No se pudo cargar la ficha');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    // Si esto falla hay que decirlo: tragarse el error deja la pantalla vacía
    // sin manera de distinguir "no hay mediciones" de "no se pudieron traer".
    api.getMediciones(id)
      .then((d) => { if (!cancelled) setPruebasData(d); })
      .catch((e) => {
        if (cancelled) return;
        setErrorPruebas(e?.message || 'No se pudieron cargar las pruebas funcionales');
        toast.error('No se pudieron cargar las pruebas funcionales');
      });
    api.getGroups()
      .then((g) => { if (!cancelled) setGrupos(g || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  const pruebas = useMemo(() => pruebasData?.mediciones || [], [pruebasData]);

  if (loading) {
    return (
      <Layout>
        <div className="py-20 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  if (!data?.alumno) {
    return (
      <Layout>
        <Card className="border-gray-200 bg-white">
          <CardContent className="py-16 text-center space-y-4">
            <p className="text-gray-500">No se encontró la ficha de este alumno.</p>
            <Button variant="outline" onClick={() => navigate('/alumnos')}>
              Volver a Alumnos
            </Button>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  const { alumno, mediciones = [] } = data;
  const entrevistas = mediciones;
  const ultima = entrevistas[0] || null;
  const anterior = entrevistas[1] || null;
  const compPruebas = pruebasData?.comparativa || null;
  const estado = ultima?.exercise_recommendation?.estado;
  const color = colorEscenario(estado);
  const edad = edadDesde(alumno.fecha_nacimiento) ?? ultima?.age ?? null;
  const grupo = grupos.find((g) => g.id === alumno.grupo_id);
  const pendientes = queTocaAhora(alumno, entrevistas, pruebas);
  const resumenPruebas = ultimasPruebas(pruebas);

  // Historial: entrevistas y sesiones de pruebas, de la más nueva a la más vieja.
  const historial = [
    ...entrevistas.map((e, i) => ({
      tipo: 'entrevista', id: e.id, fecha: e.created_at, color: colorEscenario(e.exercise_recommendation?.estado),
      titulo: i === entrevistas.length - 1 ? 'Entrevista inicial' : 'Retest',
      detalle: getScenarioLabel(e.exercise_recommendation?.estado),
    })),
    ...pruebas.map((m) => ({
      tipo: 'pruebas', id: m.id, fecha: m.created_at, color: '#0b0b0c',
      titulo: 'Pruebas funcionales',
      detalle: Object.keys(m.resultados || {}).map((k) => NOMBRE_PRUEBA[k] || k).join(', '),
    })),
  ].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

  const accionPrincipal = () => navigate(
    entrevistas.length > 0 ? `/alumnos/${alumno.id}/retest` : `/new-evaluation?alumno=${alumno.id}`
  );

  return (
    <Layout>
      <div className="space-y-6">
        <Link
          to="/alumnos"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Alumnos
        </Link>

        {/* Encabezado: quién es, qué escenario tiene y qué se puede hacer */}
        <div
          className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 pl-6 sm:pl-7"
          data-testid="ficha-encabezado"
        >
          <span className="absolute left-0 top-0 bottom-0 w-[7px]" style={{ backgroundColor: color }} />
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="min-w-0">
              <p className={`${ROTULO} text-[11px] text-slate-500`}>Ficha del alumno</p>
              <div className="flex items-center gap-2 mt-1">
                <h1 className={`${TITULO} text-[34px] sm:text-[40px] leading-none text-slate-900 break-words`}>
                  {alumno.nombre}
                </h1>
                <button
                  type="button"
                  onClick={() => setEditando(true)}
                  className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                  title="Editar datos"
                  data-testid="alumno-editar-btn"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              </div>
              <p className="text-sm text-slate-500 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {edad != null && <span>{edad} años</span>}
                {alumno.sexo && <span>{alumno.sexo}</span>}
                {alumno.telefono && <span>{alumno.telefono}</span>}
                {grupo && <span>Grupo: {grupo.name}</span>}
                <span>Alumno desde {formatFecha(alumno.created_at)}</span>
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {estado ? (
                  <span
                    className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold text-slate-800"
                    style={{ borderColor: `${color}66`, backgroundColor: `${color}14` }}
                  >
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                    {getScenarioLabel(estado)}
                    <span className="font-normal text-slate-500">· {formatFecha(ultima.created_at)}</span>
                  </span>
                ) : (
                  <span className="text-sm text-slate-500">Sin entrevista todavía</span>
                )}
                {alumno.apto_pendiente && (
                  <span className="text-xs px-2.5 py-1 rounded-full border bg-orange-50 text-orange-800 border-orange-200">
                    Apto médico pendiente
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2.5">
              <button type="button" onClick={accionPrincipal} className={BTN_VERDE}>
                <ClipboardList className="h-4 w-4" />
                {entrevistas.length > 0 ? 'Hacer el retest' : 'Entrevistar'}
              </button>
              <button type="button" onClick={() => navigate(`/alumnos/${alumno.id}/pruebas`)} className={BTN_CLARO}>
                <Activity className="h-4 w-4" />
                Pruebas funcionales
              </button>
            </div>
          </div>
        </div>

        <EditarAlumnoDialog
          alumno={alumno}
          open={editando}
          onOpenChange={setEditando}
          onGuardado={(a) => setData((d) => ({ ...d, alumno: { ...d.alumno, ...a } }))}
        />

        {/* Pestañas */}
        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" role="tablist">
          {PESTANAS.map((p) => {
            const n = p.key === 'entrevistas' ? entrevistas.length : p.key === 'pruebas' ? pruebas.length : null;
            return (
              <button
                key={p.key}
                type="button"
                role="tab"
                aria-selected={pestana === p.key}
                onClick={() => irA(p.key)}
                className={`px-4 py-2.5 -mb-px border-b-[3px] whitespace-nowrap ${ROTULO} text-[12.5px] transition-colors ${
                  pestana === p.key
                    ? 'border-[#27AE60] text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
                data-testid={`ficha-tab-${p.key}`}
              >
                {p.label}{n != null ? ` (${n})` : ''}
              </button>
            );
          })}
        </div>

        {/* ---------------------------------------------------------- Resumen */}
        {pestana === 'resumen' && (
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-5">
            <div className="space-y-5 min-w-0">
              <div className="rounded-2xl border border-[#d9cfb6] bg-[#f3ecdc] p-5">
                <h3 className={`${TITULO} text-[19px] text-[#14110d] mb-3`}>Qué toca ahora</h3>
                {pendientes.length === 0 ? (
                  <p className="text-sm text-[#3d362b]">
                    Nada pendiente. Próximo retest: {formatFecha(alumno.proximo_retest)}.
                  </p>
                ) : (
                  <ol className="space-y-2">
                    {pendientes.map((t, i) => (
                      <li key={t} className="flex gap-2.5 text-sm text-[#2a251d]">
                        <span className="shrink-0 h-5 w-5 rounded-full border-2 border-[#0b0b0c] bg-[#8CF0B4] text-[11px] font-bold flex items-center justify-center">
                          {i + 1}
                        </span>
                        <span>{t}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              <Caja titulo="Qué cambió">
                {ultima && anterior ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500 -mt-1">
                      {formatFecha(anterior.created_at)} → {formatFecha(ultima.created_at)}
                    </p>
                    <AdherenciaRetest adherencia={ultima.retest_adherencia} />
                    <ComparativaEntrevistas anterior={anterior} actual={ultima} />
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    {entrevistas.length === 0
                      ? 'Este alumno todavía no tiene entrevistas registradas.'
                      : 'Con una sola entrevista no hay con qué comparar. La comparativa aparece a partir del primer retest.'}
                  </p>
                )}
              </Caja>
            </div>

            <div className="space-y-5 min-w-0">
              <Caja
                titulo="Últimas pruebas"
                accion={pruebas.length > 0 && (
                  <button type="button" onClick={() => irA('pruebas')} className="text-sm text-slate-500 hover:text-slate-800 underline">
                    Ver todo
                  </button>
                )}
              >
                {errorPruebas ? (
                  <p className="text-sm text-red-700">{errorPruebas}</p>
                ) : resumenPruebas.length === 0 ? (
                  <p className="text-sm text-gray-500">Todavía no tiene pruebas funcionales cargadas.</p>
                ) : (
                  <div className="space-y-2">
                    {resumenPruebas.map((p) => (
                      <div
                        key={p.clave}
                        className="relative overflow-hidden flex items-center justify-between gap-3 rounded-xl border border-slate-200 pl-4 pr-3 py-2.5"
                      >
                        <span className={`absolute left-0 top-0 bottom-0 w-1 ${p.aviso ? 'bg-amber-400' : 'bg-[#27AE60]'}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800">{p.nombre}</p>
                          <p className="text-xs text-slate-500">
                            {formatFecha(p.fecha)}{p.detalle ? ` · ${p.detalle}` : ''}
                          </p>
                        </div>
                        <span className={`${TITULO} text-[24px] text-slate-900 tracking-normal`}>{p.valor}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Caja>

              <Caja titulo="Historial">
                {historial.length === 0 ? (
                  <p className="text-sm text-gray-500">Sin registros todavía.</p>
                ) : (
                  <div className="relative pl-6">
                    <span className="absolute left-[7px] top-1.5 bottom-1.5 w-0.5 bg-slate-200" />
                    {historial.map((h) => (
                      <button
                        key={`${h.tipo}-${h.id}`}
                        type="button"
                        onClick={() => (h.tipo === 'entrevista' ? navigate(`/evaluation/${h.id}`) : irA('pruebas'))}
                        className="relative block w-full text-left pb-3.5 last:pb-0 group"
                      >
                        <span
                          className="absolute -left-[22px] top-1 h-3 w-3 rounded-full ring-2 ring-white"
                          style={{ backgroundColor: h.color }}
                        />
                        <span className="text-sm font-semibold text-slate-800 group-hover:underline">{h.titulo}</span>
                        <span className="text-xs text-slate-500 ml-2">{formatFecha(h.fecha)}</span>
                        {h.detalle && <span className="block text-xs text-slate-500">{h.detalle}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </Caja>
            </div>
          </div>
        )}

        {/* ------------------------------------------------------ Entrevistas */}
        {pestana === 'entrevistas' && (
          <Caja
            titulo="Entrevistas"
            accion={
              <button type="button" onClick={accionPrincipal} className={BTN_VERDE}>
                <ClipboardList className="h-4 w-4" />
                {entrevistas.length > 0 ? 'Hacer el retest' : 'Entrevistar'}
              </button>
            }
          >
            {entrevistas.length === 0 ? (
              <p className="text-sm text-gray-500">Este alumno todavía no tiene entrevistas registradas.</p>
            ) : (
              <div className="space-y-2">
                {entrevistas.map((m, i) => {
                  const c = colorEscenario(m.exercise_recommendation?.estado);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => navigate(`/evaluation/${m.id}`)}
                      className="w-full flex items-center justify-between gap-3 rounded-xl border border-slate-200 border-l-[5px] px-4 py-3 text-left hover:border-slate-300 transition"
                      style={{ borderLeftColor: c }}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800">
                          {i === entrevistas.length - 1 ? 'Entrevista inicial' : 'Retest'}
                          <span className="ml-2 font-normal text-slate-500">{formatFecha(m.created_at)}</span>
                        </p>
                        <p className="text-sm text-slate-500">
                          {getScenarioLabel(m.exercise_recommendation?.estado)}
                          {m.activity_level && ` · ${getActivityLabel(m.activity_level)}`}
                          {num(m.weight) !== null && ` · ${num(m.weight).toFixed(1)} kg`}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-300 shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
          </Caja>
        )}

        {/* ---------------------------------------------------------- Pruebas */}
        {pestana === 'pruebas' && (
          <Caja
            titulo="Pruebas funcionales"
            accion={
              <button type="button" onClick={() => navigate(`/alumnos/${alumno.id}/pruebas`)} className={BTN_CLARO}>
                <Activity className="h-4 w-4" />
                Cargar pruebas
              </button>
            }
          >
            {errorPruebas ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm text-red-800 font-medium">No se pudieron cargar las pruebas funcionales</p>
                <p className="text-xs text-red-700 mt-1">{errorPruebas}</p>
              </div>
            ) : pruebas.length === 0 ? (
              <p className="text-sm text-gray-500">Este alumno todavía no tiene pruebas funcionales cargadas.</p>
            ) : (
              <PruebasFuncionalesDetalle pruebas={pruebas} comparativa={compPruebas} />
            )}
          </Caja>
        )}

        {/* --------------------------------------------------------- Informes */}
        {pestana === 'informes' && (
          <div className="space-y-5">
            <Caja titulo="Informe de seguimiento">
              <p className="text-sm text-slate-600 mb-3">
                La evolución de la persona: sus entrevistas y sus pruebas funcionales, con
                los cambios entre la primera y la última.
              </p>
              {(entrevistas.length > 0 || pruebas.length > 0) ? (
                <button
                  type="button"
                  onClick={descargarSeguimiento}
                  disabled={descargando}
                  className={BTN_CLARO}
                  data-testid="alumno-informe-seguimiento-btn"
                >
                  {descargando ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                  Bajar el informe de seguimiento
                </button>
              ) : (
                <p className="text-sm text-gray-500">Aparece cuando haya una entrevista o pruebas cargadas.</p>
              )}
            </Caja>
            <Caja titulo="Informe de cada entrevista">
              <p className="text-sm text-slate-600 mb-3">
                Una hoja, para el alumno y para el médico. Se baja desde cada entrevista.
              </p>
              {entrevistas.length === 0 ? (
                <p className="text-sm text-gray-500">Todavía no hay entrevistas.</p>
              ) : (
                <div className="space-y-2">
                  {entrevistas.map((m, i) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => navigate(`/evaluation/${m.id}`)}
                      className="w-full flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-2.5 text-left hover:border-slate-300 transition"
                    >
                      <span className="text-sm text-slate-800">
                        {i === entrevistas.length - 1 ? 'Entrevista inicial' : 'Retest'} del {formatFecha(m.created_at)}
                      </span>
                      <span className="text-sm text-slate-500 inline-flex items-center gap-1">
                        Abrir <ChevronRight className="h-4 w-4" />
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </Caja>
          </div>
        )}
      </div>
    </Layout>
  );
}
